import React, { useState, useEffect } from 'react';
import { Button } from './ui/Button';
import { Card, CardContent } from './ui/Card';
import { Input } from './ui/Input';
import { Badge } from './ui/Badge';
import { Alert } from './ui/Alert';
import { toast } from 'sonner';
import { apiFetch, parseJsonResponse, isNetworkErrorResponse } from '../utils/api';

const API_BASE_URL = process.env.NODE_ENV === 'production' ? '/api' : 'http://hiomenu.local/api';

interface SliderImage {
  id: number;
  pantalla_id: number | null;
  establishment_id: number | null;
  is_shared: number;
  image_path: string;
  image_url?: string;
  display_order: number;
  duration: number;
  is_active: number;
  image_type?: 'global' | 'owner' | 'shared' | 'screen' | 'establishment';
}

interface SliderImageManagerProps {
  screenId: number | null;
  establishmentId: number;
  token: string;
}

export const SliderImageManager: React.FC<SliderImageManagerProps> = ({
  screenId,
  establishmentId,
  token
}) => {
  const [images, setImages] = useState<SliderImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [availableScreens, setAvailableScreens] = useState<any[]>([]);
  const [loadingScreens, setLoadingScreens] = useState(false);
  const [editingImage, setEditingImage] = useState<SliderImage | null>(null);
  const [editingDuration, setEditingDuration] = useState<number>(5);
  const [editingDisplayOrder, setEditingDisplayOrder] = useState<number>(0);
  const [editingIsActive, setEditingIsActive] = useState<number>(1);
  const [replacingImage, setReplacingImage] = useState(false);

  useEffect(() => {
    if (screenId) {
      loadImages();
    }
  }, [screenId, establishmentId]);

  const loadImages = async () => {
    if (!screenId) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        pantalla_id: screenId.toString()
      });
      const route = `slider-images/list&${params}`;
      const res = await apiFetch(route, {}, token);
      
      if (isNetworkErrorResponse(res)) {
        toast.error('Error de conexión al cargar imágenes');
        return;
      }

      const json = await parseJsonResponse(res);
      if (json.error) {
        toast.error(json.error);
        return;
      }

      const responseData = json.data || json;
      setImages(responseData.images || []);
    } catch (err: any) {
      console.error('Error cargando imágenes:', err);
      toast.error('Error al cargar imágenes');
    } finally {
      setLoading(false);
    }
  };

  const loadScreensForCopy = async () => {
    if (!token || !establishmentId) return;
    
    setLoadingScreens(true);
    try {
      const params = new URLSearchParams({
        establishment_id: establishmentId.toString()
      });
      const route = `screens/list&${params}`;
      const res = await apiFetch(route, {}, token);
      
      if (isNetworkErrorResponse(res)) {
        toast.error('Error de conexión al cargar pantallas');
        return;
      }

      const json = await parseJsonResponse(res);
      if (json.error) {
        toast.error(json.error);
        return;
      }

      const allScreens = json.data?.screens || [];
      // Filtrar solo pantallas publicitarias y excluir la actual
      const filteredScreens = allScreens.filter((s: any) => 
        s.content_type === 'advertising_only' && s.id !== screenId
      );
      
      setAvailableScreens(filteredScreens);
    } catch (err: any) {
      console.error('Error cargando pantallas para copiar:', err);
      toast.error('Error al cargar pantallas disponibles');
    } finally {
      setLoadingScreens(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('slider_image', file);

      const res = await fetch(`${API_BASE_URL}.php?route=slider-images/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      // Leer primero el texto de la respuesta
      const text = await res.text();
      let json;
      
      try {
        json = JSON.parse(text);
      } catch (e) {
        throw new Error(`Error del servidor (${res.status}): ${text.substring(0, 200)}`);
      }
      
      if (!res.ok || json.error) {
        // Convertir error a string correctamente
        const errorMessage = typeof json.error === 'string' 
          ? json.error 
          : json.error === true 
            ? 'Error al subir imagen'
            : json.message || 'Error al subir imagen';
        throw new Error(errorMessage);
      }

      const imagePath = json.data?.image_path || json.image_path;
      
      if (!imagePath) {
        throw new Error('No se recibió la ruta de la imagen del servidor');
      }
      
      // Crear la imagen en la base de datos
      await createImage(imagePath);
      
      toast.success('Imagen subida exitosamente');
      await loadImages();
    } catch (err: any) {
      console.error('Error subiendo imagen:', err);
      const errorMessage = err.message || 'Error al subir imagen';
      toast.error(errorMessage);
    } finally {
      setUploading(false);
      // Resetear el input
      if (e.target) {
        e.target.value = '';
      }
    }
  };

  const createImage = async (imagePath: string) => {
    if (!screenId) return;

    try {
      const res = await apiFetch(
        'slider-images/create',
        {
          method: 'POST',
          body: JSON.stringify({
            pantalla_id: screenId,
            establishment_id: establishmentId,
            is_shared: 0,
            image_path: imagePath,
            display_order: images.length,
            duration: 5,
            is_active: 1
          })
        },
        token
      );

      if (isNetworkErrorResponse(res)) {
        throw new Error('Error de conexión');
      }

      const json = await parseJsonResponse(res);
      if (json.error) {
        throw new Error(typeof json.error === 'string' ? json.error : 'Error al crear imagen');
      }
    } catch (err: any) {
      throw err;
    }
  };

  const updateImage = async (imageId: number, updates: Partial<SliderImage>) => {
    try {
      const res = await apiFetch(
        'slider-images/update',
        {
          method: 'POST',
          body: JSON.stringify({
            id: imageId,
            ...updates
          })
        },
        token
      );

      if (isNetworkErrorResponse(res)) {
        throw new Error('Error de conexión');
      }

      const json = await parseJsonResponse(res);
      if (json.error) {
        throw new Error(typeof json.error === 'string' ? json.error : 'Error al actualizar imagen');
      }

      toast.success('Imagen actualizada exitosamente');
      await loadImages();
    } catch (err: any) {
      console.error('Error actualizando imagen:', err);
      toast.error(err.message || 'Error al actualizar imagen');
    }
  };

  const openEditModal = (image: SliderImage) => {
    setEditingImage(image);
    setEditingDuration(image.duration);
    setEditingDisplayOrder(image.display_order);
    setEditingIsActive(image.is_active);
    setReplacingImage(false);
  };

  const handleReplaceImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editingImage) return;

    setReplacingImage(true);
    try {
      const formData = new FormData();
      formData.append('slider_image', file);

      const res = await fetch(`${API_BASE_URL}.php?route=slider-images/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const text = await res.text();
      let json;
      
      try {
        json = JSON.parse(text);
      } catch (e) {
        throw new Error(`Error del servidor (${res.status}): ${text.substring(0, 200)}`);
      }
      
      if (!res.ok || json.error) {
        const errorMessage = typeof json.error === 'string' 
          ? json.error 
          : json.error === true 
            ? 'Error al subir imagen'
            : json.message || 'Error al subir imagen';
        throw new Error(errorMessage);
      }

      const imagePath = json.data?.image_path || json.image_path;
      
      if (!imagePath) {
        throw new Error('No se recibió la ruta de la imagen del servidor');
      }
      
      // Actualizar la imagen con la nueva ruta
      await updateImage(editingImage.id, { image_path: imagePath });
      toast.success('Imagen reemplazada exitosamente');
      
      // Cerrar modal y recargar
      setEditingImage(null);
      await loadImages();
    } catch (err: any) {
      console.error('Error reemplazando imagen:', err);
      toast.error(err.message || 'Error al reemplazar imagen');
    } finally {
      setReplacingImage(false);
      if (e.target) {
        e.target.value = '';
      }
    }
  };

  const handleSaveEdit = async () => {
    if (!editingImage) return;

    try {
      await updateImage(editingImage.id, {
        duration: editingDuration,
        display_order: editingDisplayOrder,
        is_active: editingIsActive
      });
      setEditingImage(null);
    } catch (err: any) {
      // El error ya se maneja en updateImage
    }
  };

  const deleteImage = async (imageId: number) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm('¿Estás seguro de eliminar esta imagen?')) return;

    try {
      const params = new URLSearchParams({
        id: imageId.toString()
      });
      const route = `slider-images/delete&${params}`;
      const res = await apiFetch(route, {}, token);

      if (isNetworkErrorResponse(res)) {
        throw new Error('Error de conexión');
      }

      const json = await parseJsonResponse(res);
      if (json.error) {
        throw new Error(typeof json.error === 'string' ? json.error : 'Error al eliminar imagen');
      }

      toast.success('Imagen eliminada exitosamente');
      await loadImages();
    } catch (err: any) {
      console.error('Error eliminando imagen:', err);
      toast.error(err.message || 'Error al eliminar imagen');
    }
  };

  const handleCopyImages = async (sourceScreenId: number) => {
    if (!screenId) return;

    try {
      const res = await apiFetch(
        'slider-images/copy',
        {
          method: 'POST',
          body: JSON.stringify({
            source_screen_id: sourceScreenId,
            target_screen_id: screenId
          })
        },
        token
      );

      if (isNetworkErrorResponse(res)) {
        throw new Error('Error de conexión');
      }

      const json = await parseJsonResponse(res);
      if (json.error) {
        throw new Error(typeof json.error === 'string' ? json.error : 'Error al copiar imágenes');
      }

      const copiedCount = (json.data as any)?.copied || 0;
      toast.success(`Se copiaron ${copiedCount} imágenes exitosamente`);
      setShowCopyModal(false);
      await loadImages();
    } catch (err: any) {
      console.error('Error copiando imágenes:', err);
      toast.error(err.message || 'Error al copiar imágenes');
    }
  };

  const moveImage = async (imageId: number, direction: 'up' | 'down') => {
    const imageIndex = images.findIndex(img => img.id === imageId);
    if (imageIndex === -1) return;

    const newIndex = direction === 'up' ? imageIndex - 1 : imageIndex + 1;
    if (newIndex < 0 || newIndex >= images.length) return;

    const image = images[imageIndex];
    const targetImage = images[newIndex];

    // Intercambiar display_order
    await Promise.all([
      updateImage(image.id, { display_order: targetImage.display_order }),
      updateImage(targetImage.id, { display_order: image.display_order })
    ]);
  };

  if (!screenId) {
    return (
      <Alert variant="warning">
        Debes guardar la pantalla primero para poder gestionar las imágenes.
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Imágenes del Slider</h3>
          <p className="text-sm text-gray-500">
            Gestiona las imágenes que se mostrarán en el slider publicitario.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setShowCopyModal(true);
              loadScreensForCopy();
            }}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Copiar Imágenes
          </Button>
          <div className="relative">
            <input
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/gif"
              onChange={handleFileUpload}
              className="hidden"
              id="slider-image-upload"
            />
            <Button 
              variant="primary" 
              size="sm" 
              disabled={uploading}
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const input = document.getElementById('slider-image-upload') as HTMLInputElement;
                if (input) {
                  input.click();
                }
              }}
            >
              {uploading ? 'Subiendo...' : 'Subir Imagen'}
            </Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-500">Cargando imágenes...</div>
      ) : images.length === 0 ? (
        <Alert variant="info">
          No hay imágenes. Sube una imagen para comenzar.
        </Alert>
      ) : (
        <div className="space-y-3">
          {images
            .sort((a, b) => a.display_order - b.display_order)
            .map((image, index) => (
              <Card key={image.id}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0">
                      {image.image_url ? (
                        <img
                          src={image.image_url}
                          alt={`Imagen ${index + 1}`}
                          className="w-24 h-24 object-cover rounded"
                        />
                      ) : (
                        <div className="w-24 h-24 bg-gray-200 rounded flex items-center justify-center">
                          <span className="text-gray-400 text-xs">Sin imagen</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <Badge variant={image.is_shared === 1 ? 'info' : 'neutral'}>
                          {image.is_shared === 1 ? 'Global' : 'Específica'}
                        </Badge>
                        <span className="text-sm text-gray-500">
                          Orden: {image.display_order + 1}
                        </span>
                      </div>
                      <div className="flex gap-2 items-center">
                        <Input
                          type="number"
                          label="Duración (segundos)"
                          value={image.duration}
                          onChange={(e) => {
                            const duration = parseInt(e.target.value, 10);
                            if (duration > 0) {
                              updateImage(image.id, { duration });
                            }
                          }}
                          className="w-32"
                          min="1"
                        />
                        <div className="flex gap-1">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => openEditModal(image)}
                          >
                            Editar
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => moveImage(image.id, 'up')}
                            disabled={index === 0}
                          >
                            ↑
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => moveImage(image.id, 'down')}
                            disabled={index === images.length - 1}
                          >
                            ↓
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => deleteImage(image.id)}
                          >
                            Eliminar
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
        </div>
      )}

      {/* Modal de edición */}
      {editingImage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Editar Imagen del Slider</h3>
            
            <div className="space-y-4">
              {/* Preview de imagen actual */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Imagen Actual
                </label>
                {editingImage.image_url ? (
                  <div className="relative w-full h-48 bg-gray-100 rounded-lg overflow-hidden border border-gray-300">
                    <img
                      src={editingImage.image_url}
                      alt="Imagen actual"
                      className="w-full h-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="w-full h-48 bg-gray-200 rounded-lg flex items-center justify-center">
                    <span className="text-gray-400">Sin imagen</span>
                  </div>
                )}
              </div>

              {/* Reemplazar imagen */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reemplazar Imagen
                </label>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/gif"
                  onChange={handleReplaceImage}
                  className="hidden"
                  id="replace-slider-image"
                  disabled={replacingImage}
                />
                <Button
                  variant="secondary"
                  size="sm"
                  type="button"
                  disabled={replacingImage}
                  onClick={() => {
                    const input = document.getElementById('replace-slider-image') as HTMLInputElement;
                    input?.click();
                  }}
                >
                  {replacingImage ? 'Subiendo...' : 'Seleccionar Nueva Imagen'}
                </Button>
              </div>

              {/* Duración */}
              <Input
                type="number"
                label="Duración (segundos)"
                value={editingDuration}
                onChange={(e) => setEditingDuration(parseInt(e.target.value, 10) || 5)}
                min="1"
              />

              {/* Orden de visualización */}
              <Input
                type="number"
                label="Orden de Visualización"
                value={editingDisplayOrder}
                onChange={(e) => setEditingDisplayOrder(parseInt(e.target.value, 10) || 0)}
                min="0"
              />

              {/* Estado activo/inactivo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Estado
                </label>
                <select
                  value={editingIsActive}
                  onChange={(e) => setEditingIsActive(parseInt(e.target.value, 10))}
                  className="w-full px-4 py-3 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value={1}>Activo</option>
                  <option value={0}>Inactivo</option>
                </select>
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <Button
                variant="primary"
                onClick={handleSaveEdit}
                className="flex-1"
              >
                Guardar Cambios
              </Button>
              <Button
                variant="secondary"
                onClick={() => setEditingImage(null)}
                className="flex-1"
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para copiar imágenes */}
      {showCopyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-96 overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Copiar Imágenes de Otra Pantalla</h3>
            {loadingScreens ? (
              <div className="text-center py-4 text-gray-500">Cargando pantallas...</div>
            ) : availableScreens.length === 0 ? (
              <Alert variant="warning">
                No hay otras pantallas publicitarias disponibles para copiar imágenes.
              </Alert>
            ) : (
              <div className="space-y-2">
                {availableScreens.map((screen) => (
                  <Card
                    key={screen.id}
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => handleCopyImages(screen.id)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-gray-800">{screen.name}</h4>
                          <p className="text-xs text-gray-500 mt-1">
                            {screen.orientation === 'horizontal' ? 'Horizontal' : 'Vertical'}
                          </p>
                        </div>
                        <Button variant="primary" size="sm">
                          Copiar
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            <div className="mt-4 flex justify-end">
              <Button variant="secondary" onClick={() => setShowCopyModal(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

